const setNativeValue = (element, value) => {
  // Find the element's own value setter. React dedupes events by wrapping the element's `.value` setter.
  // For more info see: https://github.com/facebook/react/issues/10135#issuecomment-401496776
  const { set: valueSetter } =
    Object.getOwnPropertyDescriptor(element, "value") || {};
  const prototype = Object.getPrototypeOf(element);
  const { set: prototypeValueSetter } =
    Object.getOwnPropertyDescriptor(prototype, "value") || {};

  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(element, value);
  } else if (valueSetter) {
    valueSetter.call(element, value);
  } else {
    throw new Error("The given element does not have a value setter");
  }
};

const sendKeyEvents = (window, element, key) => {
  element.dispatchEvent(
    new window.KeyboardEvent("keydown", {
      bubbles: true,
      key,
      code: "Key" + key,
    })
  );
  // IE does not support `InputEvent` but some frameworks need it (Angular, React).
  if (window.InputEvent) {
    element.dispatchEvent(
      new window.InputEvent("input", { bubbles: true, data: key })
    );
  }

  element.dispatchEvent(
    new window.KeyboardEvent("keyup", { bubbles: true, key, code: "Key" + key })
  );
};

const isElementVisible = (window, elem) => {
  const elemStyle = window.getComputedStyle(elem);
  return (
    elemStyle.visibility === "visible" &&
    !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length)
  );
};

const TAGS_PRIORITY = ["label", "input", "button"];

const isElementTagPrioritized = (elem1, elem2) =>
  TAGS_PRIORITY.indexOf(elem1.tagName.toLocaleLowerCase()) >
  TAGS_PRIORITY.indexOf(elem2.tagName.toLocaleLowerCase());

const compareElements = (window, searchKey) => (elem1, elem2) => {
  const lowPriorityCursors = ["not-allowed", "no-drop"];

  const elem1Visibility = isElementVisible(window, elem1);
  const elem2Visibility = isElementVisible(window, elem2);

  // Give higher priority to visible elements.
  if (elem1Visibility !== elem2Visibility) {
    if (elem1Visibility) {
      return -1;
    } else if (elem2Visibility) {
      return 1;
    }
  }

  const elem1Style = window.getComputedStyle(elem1);
  const elem2Style = window.getComputedStyle(elem2);
  if (
    lowPriorityCursors.includes(elem1Style.cursor) !==
    lowPriorityCursors.includes(elem2Style.cursor)
  ) {
    return lowPriorityCursors.includes(elem2Style.cursor) ? 1 : -1;
  }

  const elem1Text = getElementNormalizedText(elem1);
  const elem2Text = getElementNormalizedText(elem2);

  // Give higher priority to exact match on text.
  if (elem1Text !== "" && elem2Text !== "" && elem1Text !== elem2Text) {
    if (elem1Text === searchKey.trim().toLocaleLowerCase()) {
      return -1;
    } else if (elem2Text === searchKey.trim().toLocaleLowerCase()) {
      return 1;
    }
  }

  if (isElementTagPrioritized(elem1, elem2)) {
    return -1;
  } else if (isElementTagPrioritized(elem2, elem1)) {
    return 1;
  }

  // If both elements include the text - Get the one closer to the text we are looking for.
  if (
    containsWords(elem1Text, searchKey) &&
    containsWords(elem2Text, searchKey)
  ) {
    return searchKey.length / elem1Text.length >
      searchKey.length / elem2Text.length
      ? -1
      : 1;
  }

  if (containsWords(elem1Text, searchKey)) {
    return -1;
  }

  if (containsWords(elem2Text, searchKey)) {
    return 1;
  }

  return 0;
};

const containsWords = (haystack, words) =>
  haystack &&
  !!haystack
    .replace(/[(|)]/g, "") // Remove parenthesis as it interferes with word boundaries.
    .match(new RegExp("\\b" + words.replace(/[(|)]/g, "") + "\\b", "i"));

const normalizeText = (text) => text.trim().toLocaleLowerCase();

const getElementNormalizedText = (element) =>
  Array.from(element.childNodes)
    .filter((n) => n.nodeType === Node.TEXT_NODE) // Get all direct text nodes.
    .map((n) => n.nodeValue)
    .join("")
    .trim()
    .toLocaleLowerCase();

const matchElementAttribute = (elem, attr, searchKey) =>
  elem.getAttribute(attr) &&
  containsWords(
    elem.getAttribute(attr).toLocaleLowerCase(),
    searchKey.toLocaleLowerCase()
  );

const getElementCoordinates = (elem) => {
  const { top, bottom, right, left } = elem.getBoundingClientRect();
  return [
    [left, bottom],
    [left, top],
    [right, bottom],
    [right, top],
  ];
};

const arrayProduct = (array1, array2) =>
  array1.flatMap((elem1) => array2.map((elem2) => [elem1, elem2]));

const euclideanDistance = ([x1, y1], [x2, y2]) =>
  Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));

const getMinimalDistance = (elem1, elem2) => {
  const coordinatePairs = arrayProduct(
    getElementCoordinates(elem1),
    getElementCoordinates(elem2)
  );

  return Math.min(
    ...coordinatePairs.map(([p1, p2]) => euclideanDistance(p1, p2))
  );
};

const swapAroundPivotExcludingPivot = (array, pivot) => {
  if (!pivot) {
    return array;
  }
  const attentionIndex = array.indexOf(pivot);
  return attentionIndex < 0
    ? array
    : [...array.slice(attentionIndex + 1), ...array.slice(0, attentionIndex)];
};

const findElement = (window, root, searchKey, optional, attention) => {
  const attrList = [
    "title",
    "name",
    "placeholder",
    "class",
    "id",
    "value",
    "aria-label",
    "for",
  ];

  const elements = swapAroundPivotExcludingPivot(
    Array.from(root.querySelectorAll("*")),
    attention
  ).filter((elem) => {
    if (containsWords(getElementNormalizedText(elem), searchKey)) {
      return true;
    }
    const datasetList = Object.keys(elem.dataset).map((att) => "data-" + att);
    const allAttributes = [...attrList, ...datasetList];
    // Try marching on attributes (from above list).
    return allAttributes.find((attr) =>
      matchElementAttribute(elem, attr, searchKey)
    );
  });

  if (!elements.length) {
    if (root.parentNode !== window.document.body.parentNode) {
      return findElement(
        window,
        root.parentNode || window.document.body,
        searchKey,
        optional,
        attention
      );
    }
    if (optional) {
      console.warn("could not find element, will continue since is optional.");
      return null;
    }
    throw new Error("could not find element " + searchKey);
  }

  elements.sort(compareElements(window, searchKey));

  console.log(elements);

  return elements[0];
};

const findInputForLabel = (window, label) => {
  // If element has a `for` attribute (e.g. label) forward the selection.
  if (label.getAttribute("for")) {
    const referencedInput = window.document.getElementById(
      label.getAttribute("for")
    );
    if (referencedInput) {
      return referencedInput;
    }
  }

  const allInputs = Array.from(
    window.document.querySelectorAll("input, select")
  );

  // Find the closest (euclidean distance) input to the label.
  return allInputs
    .filter((elem) => isElementVisible(window, elem) && !elem.readOnly)
    .reduce(
      ({ closestInput, closestDistance }, currentInput) => {
        const distance = getMinimalDistance(label, currentInput);
        return distance < closestDistance
          ? { closestInput: currentInput, closestDistance: distance }
          : { closestInput, closestDistance };
      },
      { closestInput: null, closestDistance: Infinity }
    ).closestInput;
};

const createEmptyState = () => ({ attention: null });

const setElement = (element, state) => ({
  ...state,
  attention: element,
});

const selectElement =
  (window) =>
  ({ key, optional }, state) => {
    const elem = findElement(
      window,
      state.attention || window.document.body,
      key,
      optional,
      state.attention
    );
    if (elem && ["label", "td"].includes(elem.tagName.toLocaleLowerCase())) {
      console.log("found a label", elem, "finding matching input");
      const matchedInput = findInputForLabel(window, elem);
      if (matchedInput) {
        return setElement(matchedInput, state);
      }
    }
    return setElement(elem || state.attention || window.document.body, state);
  };

const setInput =
  (window) =>
  ({ text }, state) => {
    const input = state.attention;

    if (!input) {
      console.error(
        `cannot perform input action. no element in stack. please use select.`
      );
      return;
    }

    console.log(`(input) setting ${input} to ${text}`);
    input.dispatchEvent(new window.FocusEvent("focus", { bubbles: true }));
    if (input.scrollIntoView) {
      input.scrollIntoView({ block: "center", inline: "center" });
    }
    if (input.tagName.toLocaleLowerCase() === "select") {
      const option = Array.from(input.options).find((x) =>
        containsWords(x.text, text)
      );
      setNativeValue(input, option.value);
      input.dispatchEvent(new window.Event("change", { bubbles: true }));
    } else {
      input.dispatchEvent(new window.Event("click", { bubbles: true }));
      input.dispatchEvent(new window.FocusEvent("focus", { bubbles: true }));
      setNativeValue(input, text);
      if (text === "%0A") {
        // Enter
        input.dispatchEvent(
          new window.KeyboardEvent("keydown", {
            bubbles: true,
            key: "Enter",
            code: "Enter",
            which: 13,
            keyCode: 13,
          })
        );
      } else {
        Array.from(decodeURIComponent(text)).forEach((c) =>
          sendKeyEvents(window, input, c)
        );
        input.dispatchEvent(new window.Event("change", { bubbles: true }));
      }
    }
    return state;
  };

const click = (window) => (_, state) => {
  const button = state.attention;
  if (!button) {
    console.error(
      `cannot perform click action. no element in stack. please use select.`
    );
    return;
  }
  console.log(`simulating click on ${button}`);
  button.dispatchEvent(new window.MouseEvent("click", { bubbles: true }));
  button.dispatchEvent(new window.FocusEvent("focus", { bubbles: true }));
  button.dispatchEvent(new window.MouseEvent("mousedown", { bubbles: true }));
  button.scrollIntoView({ block: "center", inline: "center" });
  return state;
};

const redirect = ({ url }, state) => {
  if (!url) {
    console.error(`cannot perform redirect action. Missing url parameter.`);
    return;
  }

  console.log(`simulating redirect to ${url}`);
  window.location.assign(url);
  return state;
};

const parseValue = (value) => {
  if (!isNaN(Number.parseFloat(value))) {
    return Number.parseFloat(value);
  }

  if (!isNaN(new Date(value).getDate())) {
    return new Date(value).getTime();
  }

  return value;
};

const evaluatePredicate = ({ operator, arg, negate }, state) => {
  const currentElement = state.attention;

  if (!currentElement) {
    throw new Error("cannot evaluate predicate, no element selected");
  }

  let parsedElementValue = parseValue(getElementNormalizedText(currentElement));

  if (parsedElementValue === "") {
    console.log("Couldn't find valid text in text nodes. Trying innerText.");
    parsedElementValue = parseValue(normalizeText(currentElement.innerText));
  }

  const parsedArg = parseValue(arg);

  const result =
    negate ^
    {
      gt: parsedArg > parsedElementValue,
      lt: parsedArg < parsedElementValue,
      eq: parsedArg === parsedElementValue,
    }[operator];

  console.log(
    `comparing ${parsedArg} (original ${arg}) ${operator} ${parsedElementValue} (Negate: ${negate}) ==> ${
      result ? "True" : "False"
    }`
  );

  return result;
};

const browserWhile = async (
  { predicate, commandsBefore, commandsAfter, maxIterations },
  state
) => {
  let count = 0;

  console.group(`WHILE (maxIterations==${maxIterations}`);

  while (count < maxIterations) {
    let scopedState = setElement(state.attention, createEmptyState());
    console.log(`While loop iteration: ${count}`);
    scopedState = await runBrowserCommandsInternal(commandsBefore, scopedState);

    if (!evaluatePredicate(predicate, scopedState)) {
      break;
    }

    await runBrowserCommandsInternal(commandsAfter, scopedState);
    count++;
  }

  console.groupEnd();

  return state;
};

const wait = ({ seconds }, state) =>
  new Promise((resolve) => setTimeout(() => resolve(state), seconds * 1000));

const handleRead = (_, state) => ({
  readResult: state.attention.innerText,
  ...state,
});

const readFromWindow = ({ key }, state) => ({
  ...state,
  readResult: window[key],
});

const scroll = ({ x, y }, state) => {
  state.attention.scroll(
    x * state.attention.scrollWidth,
    y * state.attention.scrollHeight
  );
  return state;
};

const runCommand = (window, { type, ...rest }, state) =>
  ({
    wait,
    select: selectElement(window),
    input: setInput(window),
    click: click(window),
    redirect,
    browser_while: browserWhile,
    read: handleRead,
    read_from_window: readFromWindow,
    scroll,
  }[type](rest, state));

const runBrowserCommandsInternal = async (
  window,
  commands,
  state,
  sendReadResult
) => {
  const commandsCopy = [...commands];
  let command = commandsCopy.shift();
  while (command) {
    console.log(`running command ${JSON.stringify(command)}`);
    if (command.type === "hold_for_redirect") {
      console.log(`Saving commands to localStorage`);
      setCommandsToLocalStorage(commandsCopy);
      break;
    }
    if (command.type === "send_read_result") {
      state = await sendReadResult(state.readResult);
      break;
    }
    state = await runCommand(window, command, state);
    console.log(state);
    command = commandsCopy.shift();
  }
  return state;
};

const hasLocalStorage = (() => {
  const test = "testlocal";
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
})();

const setCommandsToLocalStorage = (commands) => {
  if (!hasLocalStorage) return;
  localStorage.setItem("browserCommands", JSON.stringify(commands));
};

const popCommandFromLocalStorage = () => {
  if (!hasLocalStorage || !localStorage.browserCommands) {
    console.log("LocalStorage is empty");
    return;
  }
  const commands = JSON.parse(localStorage.browserCommands);
  localStorage.removeItem("browserCommands");
  return commands;
};

const getCommandsFromURL = () => {
  const params = new URLSearchParams(document.location.search.substring(1));
  if (!params.has("url_commands")) {
    console.log("No URL commands found");
    return;
  }
  return JSON.parse(window.atob(params.get("url_commands")));
};

const runBrowserCommands = async (window, commands, sendReadResult) => {
  console.log("commands:", JSON.stringify(commands));
  console.groupCollapsed("Running Browser Commands");
  let result;
  try {
    result = await runBrowserCommandsInternal(
      window,
      commands,
      createEmptyState(),
      sendReadResult
    );
  } catch (err) {
    console.error(`error while running browser commands. stopping.`);
    console.error(err);
  }
  console.groupEnd();
  return result;
};

const getElementValue = async (window, command) => {
  console.groupCollapsed("Running Browser Commands");
  let state = null;
  try {
    state = await selectElement(window)(command, createEmptyState());
  } catch (err) {
    console.error(`error while running get element. stopping.`);
    console.error(err);
  }
  console.groupEnd();
  if (state && state.attention) {
    return state.attention.value;
  }
  console.groupEnd();
};

const executeStoredCommands = (window) =>
  runBrowserCommands(
    window,
    getCommandsFromURL() || popCommandFromLocalStorage() || []
  );

export { runBrowserCommands, getElementValue, executeStoredCommands };
